import { Text } from '@sitecore-jss/sitecore-jss-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Carousel, { CarouselSlide } from 'src/components/Common/Carousel';
import { useBodyContext } from 'src/components/Common/ContentWrapper/context';
import LazyImage from 'src/components/Common/LazyImage';
import { ModalComponent } from 'src/components/Common/Modal';
import track from 'src/lib/Analytics';
import useMediaQuery from 'src/lib/Hooks/useMediaQuery';
import CurrentSlideIndicator from './components/CurrentSlideIndicator';
import { ModalSlideshowTypes } from './types';

const ModalSlideshow = ({ id, items }: ModalSlideshowTypes) => {
  const { dispatch, state } = useBodyContext();
  const { activeId, isOpen } = state.modal;
  const isMobile = !useMediaQuery('md');
  const shouldDisplayModal = activeId === id && isOpen;
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      track.component({
        category: 'modal_slideshow',
        action: location.pathname,
        label: items[0]?.headline.value ?? '',
        guid: id,
        event: 'event-click',
      });
    }
  }, [isOpen]);

  const slideshowTracking = ({
    slideDirection,
    activeSlide,
  }: {
    slideDirection: string;
    activeSlide: number;
  }) => {
    track.component({
      category: 'modal_slideshow',
      action: `${slideDirection === 'backward' ? 'left' : 'right'}-|-${activeSlide + 1}`,
      label: items[activeSlide]?.headline?.value as string,
      guid: id,
      event: 'event-click',
    });
  };

  return (
    <ModalComponent
      controls={{ isTransparent: true, preventScroll: true }}
      isActive={shouldDisplayModal}
      onCloseHandler={() => dispatch({ type: 'modalClose' })}
    >
      <Carousel
        controls={{
          arrowBelow: true,
          arrowColor: 'text-white',
          delay: 300,
          height: isMobile ? '90vh' : 700,
          hideSlideIndicator: true,
          // custom positiing of indicator component (CurrentSlideIndicator)
        }}
        onChange={({ activeSlide, slideDirection }) => {
          if (isOpen) {
            slideshowTracking({ activeSlide, slideDirection });
          }
        }}
      >
        {items.map(({ headline, subhead, image }, index) => (
          <CarouselSlide key={index}>
            <div className="w-full flex flex-col justify-center">
              <Text className="text-white" field={headline} tag="h2" />
              <div style={{ maxHeight: '75%' }}>
                <LazyImage
                  className="object-contain h-full w-full"
                  data-testid="slideshow-image"
                  {...image}
                />
              </div>
              <CurrentSlideIndicator />
              <Text className="mt-48 text-center text-white" field={subhead} tag="p" />
            </div>
          </CarouselSlide>
        ))}
      </Carousel>
    </ModalComponent>
  );
};

export default ModalSlideshow;
